import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { ICONS } from './ui';

// Receipt counts per posted invoice, shared by every InvoiceExtras on screen.
// Requests are batched (one call for the invoices that appear together) and
// remembered for the session.
const counts = new Map();   // invoiceNo -> number
const pending = new Set();  // invoiceNos waiting for the next batch
const listeners = new Set();
let timer = null;

async function flush() {
  timer = null;
  const batch = Array.from(pending).slice(0, 100);
  try {
    const r = await api.getInvoiceReceiptCounts(batch);
    batch.forEach((no) => counts.set(no, Number(((r && r.counts) || {})[no]) || 0));
  } catch (_) {
    batch.forEach((no) => counts.set(no, 0));
  }
  batch.forEach((no) => pending.delete(no));
  listeners.forEach((f) => f());
  if (pending.size) timer = setTimeout(flush, 0);
}

function useReceiptCount(invoiceNo, posted) {
  const [, rerender] = useState(0);
  useEffect(() => {
    if (!posted || !invoiceNo) return undefined;
    const f = () => rerender((n) => n + 1);
    listeners.add(f);
    if (!counts.has(invoiceNo) && !pending.has(invoiceNo)) {
      pending.add(invoiceNo);
      if (!timer) timer = setTimeout(flush, 50);
    }
    return () => { listeners.delete(f); };
  }, [invoiceNo, posted]);
  return posted ? counts.get(invoiceNo) : undefined;
}

// Icons beside an invoice link: 🧾 the invoice's receipts as one PDF (posted
// invoices that have receipts) and 📝 the time detail notes of the invoice's
// time entries (needs the invoice's job).
export default function InvoiceExtras({ invoiceNo, jobNo, posted }) {
  const no = String(invoiceNo || '').trim();
  const job = String(jobNo || '').trim();
  const receipts = useReceiptCount(no, !!posted);
  if (!no) return null;
  return (
    <span className="invoice-extras">
      {receipts > 0 && (
        <a className="invoice-extra" href={api.invoiceReceiptsPdfUrl(no, job)} target="_blank" rel="noopener noreferrer"
          title={`Receipts for invoice ${no} (${receipts})`} aria-label={`Receipts for invoice ${no}`}>
          {ICONS.receipts}
        </a>
      )}
      {job && (
        <a className="invoice-extra" href={api.invoiceTimeNotesPdfUrl(no, job, !!posted)} target="_blank" rel="noopener noreferrer"
          title={`Time detail notes for invoice ${no}`} aria-label={`Time detail notes for invoice ${no}`}>
          {ICONS.timeNotes}
        </a>
      )}
    </span>
  );
}
